/**
 * Where the cards of the Ideas page go. A card someone moved sits where they put it, as a node
 * does on the canvas. Until anyone has moved a card, the page places them: a block is one card,
 * or a Think up card with its answers in a row, and each block takes the first free spot on the
 * board's grid, from the top and from the left, in the board's order. A card made on a board
 * laid out by hand is given the free spot nearest to where it was asked for.
 *
 * Pure functions on plain data, in the board's own pixels before zoom.
 */

export interface Rect { x: number; y: number; width: number; height: number }
export interface Point { x: number; y: number }

export interface LayoutCard {
    id: string;
    width: number;
}

export interface LayoutBlock {
    id: string;
    cards: LayoutCard[];
    /** Where someone put it; null while the page places it */
    at: Point | null;
}

export const COLUMN = 340;
export const GAP = 20;
/** The height a card is given before it has been measured */
export const UNMEASURED = 200;

/** The width of a card that takes so many of the board's columns */
export const cardWidth = (span: number): number => Math.max(1, span) * COLUMN + (Math.max(1, span) - 1) * GAP;

export const blockSize = (block: LayoutBlock, heights: Record<string, number>): { width: number; height: number } => ({
    width: block.cards.reduce((sum, card) => sum + card.width, 0) + (block.cards.length - 1) * GAP,
    height: Math.max(0, ...block.cards.map(card => heights[card.id] ?? UNMEASURED)),
});

export const overlaps = (a: Rect, b: Rect): boolean =>
    a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;

/** Whether two blocks are closer than the board's gap: what the page never does to two blocks it places */
export const collides = (a: Rect, b: Rect): boolean =>
    overlaps({ x: a.x - GAP + 1, y: a.y - GAP + 1, width: a.width + 2 * GAP - 2, height: a.height + 2 * GAP - 2 }, b);

const bottomOf = (placed: ReadonlyArray<Rect>): number => placed.reduce((most, rect) => Math.max(most, rect.y + rect.height), 0);

/**
 * The first spot where a block of this size touches nothing placed: the highest row first (the
 * top of the board, or just under something), then the leftmost column of the board's grid.
 */
export const firstFreeSpot = (size: { width: number; height: number }, placed: ReadonlyArray<Rect>, columns: number): Point => {
    const step = COLUMN + GAP;
    const spanColumns = Math.max(1, Math.round((size.width + GAP) / step));
    const slots = Math.max(1, columns - spanColumns + 1);
    const rows = Array.from(new Set([0, ...placed.map(rect => rect.y + rect.height + GAP)])).sort((a, b) => a - b);
    for (const y of rows) {
        for (let c = 0; c < slots; c++) {
            const rect = { x: c * step, y, ...size };
            if (!placed.some(other => collides(rect, other))) return { x: rect.x, y };
        }
    }
    return { x: 0, y: placed.length === 0 ? 0 : bottomOf(placed) + GAP };
};

/**
 * The free spot nearest to a wanted one: the wanted spot itself when nothing is in the way,
 * else beside or under the blocks around it, the board's gap apart.
 */
export const freeSpotNear = (size: { width: number; height: number }, placed: ReadonlyArray<Rect>, wanted: Point, reach: number = 2400): Point => {
    const target = { x: Math.max(0, Math.round(wanted.x)), y: Math.max(0, Math.round(wanted.y)) };
    const free = (at: Point) => !placed.some(other => collides({ ...at, ...size }, other));
    if (free(target)) return target;
    const near = placed.filter(rect =>
        rect.x < target.x + size.width + reach && rect.x + rect.width > target.x - reach
        && rect.y < target.y + size.height + reach && rect.y + rect.height > target.y - reach);
    const xs = new Set([target.x, 0, ...near.flatMap(rect => [rect.x, rect.x + rect.width + GAP, rect.x - GAP - size.width])]);
    const ys = new Set([target.y, 0, ...near.flatMap(rect => [rect.y, rect.y + rect.height + GAP, rect.y - GAP - size.height])]);
    const candidates: { x: number; y: number; d: number }[] = [];
    for (const x of xs) {
        if (x < 0) continue;
        for (const y of ys) if (y >= 0) candidates.push({ x, y, d: (x - target.x) ** 2 + (y - target.y) ** 2 });
    }
    candidates.sort((a, b) => a.d - b.d || a.y - b.y || a.x - b.x);
    for (const candidate of candidates) if (free(candidate)) return { x: candidate.x, y: candidate.y };
    return { x: target.x, y: bottomOf(placed) + GAP };
};

export interface Placement {
    /** Every card's rectangle */
    cards: Record<string, Rect>;
    /** Every block's rectangle */
    blocks: Record<string, Rect>;
    /** How far the board reaches: from its leftmost and topmost card (never past 0, where the page's own arrangement begins) to its rightmost and lowest */
    left: number;
    top: number;
    width: number;
    height: number;
}

/** Where every block and card goes: the moved ones where they were put, the rest in the first free spots, in order */
export const placeBlocks = (blocks: ReadonlyArray<LayoutBlock>, heights: Record<string, number>, columns: number): Placement => {
    const placed: Rect[] = [];
    const blockRects: Record<string, Rect> = {};
    const put = (block: LayoutBlock, at: Point) => {
        const rect = { x: at.x, y: at.y, ...blockSize(block, heights) };
        blockRects[block.id] = rect;
        placed.push(rect);
    };
    for (const block of blocks) if (block.at) put(block, block.at);
    for (const block of blocks) if (!block.at) put(block, firstFreeSpot(blockSize(block, heights), placed, columns));
    const cards: Record<string, Rect> = {};
    for (const block of blocks) {
        const rect = blockRects[block.id];
        let x = rect.x;
        for (const card of block.cards) {
            cards[card.id] = { x, y: rect.y, width: card.width, height: heights[card.id] ?? UNMEASURED };
            x += card.width + GAP;
        }
    }
    const left = placed.reduce((least, rect) => Math.min(least, rect.x), 0);
    const top = placed.reduce((least, rect) => Math.min(least, rect.y), 0);
    return {
        cards,
        blocks: blockRects,
        left,
        top,
        width: placed.reduce((most, rect) => Math.max(most, rect.x + rect.width), 0) - left,
        height: bottomOf(placed) - top,
    };
};
